import { Activity } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "../atoms/Badge";
import { Heading } from "../atoms/Heading";
import { Link } from "../atoms/Link";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";

export type ActivityItemType =
	| "batch_created"
	| "batch_anchored"
	| "event_created";

const ACTIVITY_BADGE: Record<
	ActivityItemType,
	{ label: string; variant: "default" | "secondary" | "success" | "info" }
> = {
	batch_created: { label: "Record sealed", variant: "secondary" },
	batch_anchored: { label: "Confirmed on public ledger", variant: "success" },
	event_created: { label: "Event recorded", variant: "info" },
};

export interface ActivityItem {
	id: string;
	type: ActivityItemType;
	timestamp: string;
	summary: string;
	link?: string;
}

export interface RecentActivityFeedProps {
	items: ActivityItem[];
	className?: string;
}

function RecentActivityFeed({
	items,
	className,
}: RecentActivityFeedProps): ReactNode {
	return (
		<div
			className={cn("flex flex-col gap-3", className)}
			aria-label="Recent activity feed"
		>
			<div className="flex items-center gap-2">
				<Activity className="h-4 w-4 text-mutedForeground" aria-hidden="true" />
				<Heading size="sm">Recent Activity</Heading>
			</div>
			{items.length === 0 ? (
				<Text textColor="mutedForeground" size="sm">
					No recent activity.
				</Text>
			) : (
				<ul className="flex flex-col divide-y divide-border">
					{items.map((item) => {
						const badge = ACTIVITY_BADGE[item.type];
						return (
							<li key={item.id} className="flex flex-col gap-1 py-3">
								<div className="flex flex-wrap items-center gap-2">
									<Badge variant={badge.variant}>{badge.label}</Badge>
									<Text size="xs" textColor="mutedForeground" as="span">
										{item.timestamp}
									</Text>
								</div>
								{item.link ? (
									<Link href={item.link} className="text-sm">
										{item.summary}
									</Link>
								) : (
									<Text size="sm">{item.summary}</Text>
								)}
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
}
RecentActivityFeed.displayName = "RecentActivityFeed";

export default RecentActivityFeed;
export { RecentActivityFeed };
